import { ReactComponent as ViewIcon } from "@/Images/icons/View.svg"
import { moneyFormat } from "@/utils"
import { DefaultValues, getApiDataPromise, ICardTransactionItem, selectUser, useStoreClient } from "@usher/pe-client-front-end-lib"
import clsx from "clsx"
import type { FC } from "react"
import { memo, useCallback, useEffect, useMemo, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { PayoutsFilter } from "../Exchange/PayoutsFilter"
import { ListBase } from "./ListBase"
import { PayoutsTransactionInfo } from "./PayoutsTransactionInfo"

type Props = {
  cardId: string
  pageSize: number
  isOrders?: boolean
}

const PayoutsListComponent: FC<Props> = ({ cardId, pageSize, isOrders = false }) => {
  const { $t: t } = useIntl()

  const [payoutsPage, setPayoutsPage] = useState(0)
  const [payoutsPageCount, setPayoutsPageCount] = useState(0)
  const [payoutsData, setPayoutsData] = useState<ICardTransactionItem[]>([])
  const [payoutsTotal, setPayoutsTotal] = useState(0)
  const [payoutsFilters, setPayoutsFilters] = useState({})

  const { first_name, last_name } = useStoreClient(selectUser)

  const downloadStatement = useCallback(async () => {
    try {
      const result = await fetch(DefaultValues.PAY + "api/exclient/cards.htm", {
        method: "POST",
        body: $.param({
          action: "get_payout_statement",
          card_id: cardId,
          ...(Object.keys(payoutsFilters).length > 0 ? { filter: payoutsFilters } : {}),
        }),
        headers: new Headers({
          "Content-type": "application/x-www-form-urlencoded; charset=UTF-8",
        }),
        credentials: "include",
      })

      const blob = await result.blob()

      const downloadLink = document.createElement("a")
      const fileName = `${first_name} ${last_name} card statement.pdf`
      const url = URL.createObjectURL(blob)
      downloadLink.href = url
      downloadLink.download = fileName
      downloadLink.target = "_blank"
      downloadLink.click()
    } catch (e) {
      //
    }
  }, [cardId, first_name, last_name, payoutsFilters])

  useEffect(() => {
    const offset = pageSize * payoutsPage

    getApiDataPromise(
      { count: pageSize, offset, paged: true, card_id: cardId, ...(Object.keys(payoutsFilters).length > 0 ? { filter: payoutsFilters } : {}) },
      "exclient/cards",
      "get_card_payouts",
    ).then((res) => {
      const { records, recordsTotal } = res.data ?? { records: [], recordsTotal: 0 }

      const total = recordsTotal ?? 0

      setPayoutsPageCount(Math.ceil(total / pageSize))
      setPayoutsData(records ?? [])
      setPayoutsTotal(total)
    })
  }, [cardId, payoutsPage, payoutsFilters, pageSize])

  const tableData = useMemo(() => {
    return payoutsData.map((row) => ({
      id: <div className="text-[#777e8a]">#{row.id}</div>,
      name: (
        <div className="cursor-pointer hover:text-main-main">
          <PayoutsTransactionInfo id={row.id} title={t({ id: "payout_to_card" })}>
            <FormattedMessage id="payout_to_card" />
          </PayoutsTransactionInfo>
        </div>
      ),
      created: <span>{row.date_created}</span>,
      amount: (
        <>
          <span
            className={clsx({
              "text-red-500": row.status?.toLowerCase?.()?.includes("decline"),
              "text-green-500": row.status?.toLowerCase?.()?.includes("process"),
            })}
          >
            {moneyFormat(Number(row?.amount))}{" "}
          </span>
          {row?.currency}
        </>
      ),
      card_pan: <span>{row.card_pan}</span>,
      status: (
        <span
          className={
            row.status?.toLowerCase?.()?.includes("decline")
              ? "text-red-500"
              : row.status?.toLowerCase?.()?.includes("process")
                ? "text-green-500"
                : ""
          }
        >
          {row.status}
        </span>
      ),
      view: (
        <PayoutsTransactionInfo id={row.id} title={t({ id: "payout_to_card" })}>
          <ViewIcon className="cursor-pointer" />
        </PayoutsTransactionInfo>
      ),
    }))
  }, [payoutsData, t])

  return (
    <ListBase
      pageSize={pageSize}
      data={tableData}
      total={payoutsTotal}
      page={payoutsPage}
      pageCount={payoutsPageCount}
      titleMessageId="type-payout"
      renderFilter={<PayoutsFilter onFilter={setPayoutsFilters} withPayoutsCards={isOrders} />}
      onDownloadClick={downloadStatement}
      onPageChange={setPayoutsPage}
    />
  )
}

export const PayoutsList = memo(PayoutsListComponent)
